import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MachineLearningOperationsOverviewService } from '../_services/machine-learning-operations-overview.service';

/**
 * Normalizes SwipFx API envelopes returned by controller JSON options and by
 * low-level middleware. Middleware errors may use PascalCase while controller
 * responses use camelCase. Keeping the normalization here prevents every page
 * from having to understand both shapes.
 */
@Injectable()
export class ApiResponseNormalizerInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isSwipFxApiRequest(request.url)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      map(event => {
        if (!(event instanceof HttpResponse)) {
          return event;
        }

        if (event.ok && this.shouldInvalidateMlOverview(request)) {
          Promise.resolve().then(() =>
            this.injector.get(MachineLearningOperationsOverviewService).invalidate()
          );
        }

        if (!this.isEnvelope(event.body)) {
          return event;
        }

        return event.clone({ body: this.normalizeEnvelope(event.body) });
      }),
      catchError(error => {
        if (!(error instanceof HttpErrorResponse) || !this.isEnvelope(error.error)) {
          return throwError(() => error);
        }

        return throwError(() => new HttpErrorResponse({
          error: this.normalizeEnvelope(error.error),
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined
        }));
      })
    );
  }

  private shouldInvalidateMlOverview(request: HttpRequest<unknown>): boolean {
    if (['GET', 'HEAD', 'OPTIONS'].includes(request.method.toUpperCase())) {
      return false;
    }

    const url = request.url.toLowerCase();
    return url.includes('/admin/machine-learning')
      || (url.includes('/adminwithdrawals/') && url.includes('/risk-review'));
  }

  private isSwipFxApiRequest(url: string): boolean {
    return url.toLowerCase().startsWith(environment.apiBaseUrl.toLowerCase());
  }

  private isEnvelope(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const body = value as Record<string, unknown>;
    return 'success' in body || 'Success' in body;
  }

  private normalizeEnvelope(body: Record<string, unknown>): Record<string, unknown> {
    const meta = this.pick(body, 'meta', 'Meta');
    const error = this.pick(body, 'error', 'Error');

    return {
      success: Boolean(this.pick(body, 'success', 'Success')),
      message: this.pick(body, 'message', 'Message') ?? null,
      meta: this.normalizeMeta(meta),
      data: this.pick(body, 'data', 'Data') ?? null,
      error: this.normalizeError(error)
    };
  }

  private normalizeMeta(value: unknown): unknown {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return value ?? null;
    }

    const meta = value as Record<string, unknown>;
    return {
      currentPage: this.pick(meta, 'currentPage', 'CurrentPage') ?? 0,
      maxRowsPerPage: this.pick(meta, 'maxRowsPerPage', 'MaxRowsPerPage') ?? 0,
      rowsOnCurrentPage: this.pick(meta, 'rowsOnCurrentPage', 'RowsOnCurrentPage') ?? 0,
      totalPages: this.pick(meta, 'totalPages', 'TotalPages') ?? 0,
      totalRows: this.pick(meta, 'totalRows', 'TotalRows') ?? 0
    };
  }

  private normalizeError(value: unknown): unknown {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return value ?? null;
    }

    const error = value as Record<string, unknown>;
    return {
      code: this.pick(error, 'code', 'Code') ?? null,
      details: this.pick(error, 'details', 'Details') ?? null,
      data: this.pick(error, 'data', 'Data') ?? null
    };
  }

  private pick(source: Record<string, unknown>, camelCase: string, pascalCase: string): unknown {
    return source[camelCase] !== undefined ? source[camelCase] : source[pascalCase];
  }
}
